/**
 * dimensionFieldPresenter.ts — menschenlesbare Darstellung der Dimensions-
 * Felder für Mandanten-Sichten (Übersicht/Bestätigung + Druckansicht).
 *
 * Die 5 Freitext-Dimensionen speichern ihre Werte serialisiert als Strings
 * (bool → "true"/"false", Zahlen als String, Listen als JSON) unter den
 * Schlüsseln aus `dimensionScoreInput` (IDENTITY_KEYS …). Für den Moment, in
 * dem der Mandant die Richtigkeit seiner Angaben rechtsverbindlich bestätigt,
 * dürfen dort KEINE rohen englischen Feldnamen und keine `true`/`false`- oder
 * JSON-Werte stehen. Diese Datei mappt jeden bekannten Feldschlüssel auf ein
 * deutsches Label + einen formatierten Wert.
 *
 * Bewusst als pure Funktionen ohne Persistenz — dieselbe Ausgabe in Overview
 * und Print.
 */
import type { DimensionKey } from '../../components/hub/dimensions';
import { de } from '../i18n/de';
import { parseList, parseNum, parseOptionalBool } from './dimensionScoreInput';

const df = de.dimensionFields;

type FieldKind =
  | { readonly kind: 'text' }
  | { readonly kind: 'bool' }
  | { readonly kind: 'num'; readonly suffix?: string }
  | { readonly kind: 'enum'; readonly options: Readonly<Record<string, string>> }
  | { readonly kind: 'traits' }
  | { readonly kind: 'qualifications' }
  | { readonly kind: 'topClients' }
  | { readonly kind: 'stringList' };

type FieldSpec = FieldKind & { readonly label: string };

const YES = df.common.yes;
const NO = df.common.no;

/** Vollständige Feld-Spezifikation je Dimension (Schlüssel = Store-Feldschlüssel). */
const SPECS: Record<Exclude<DimensionKey, 'resources'>, Record<string, FieldSpec>> = {
  identity: {
    qualifications: { kind: 'qualifications', label: df.identity.qualificationsLabel },
    industryExperienceYears: {
      kind: 'num',
      suffix: df.identity.industryExperienceYearsSuffix,
      label: df.identity.industryExperienceYearsLabel,
    },
    hasEntrepreneurialExperience: {
      kind: 'bool',
      label: df.identity.hasEntrepreneurialExperienceLabel,
    },
    yearsSelfEmployed: {
      kind: 'num',
      suffix: df.identity.yearsSelfEmployedSuffix,
      label: df.identity.yearsSelfEmployedLabel,
    },
    usp: { kind: 'text', label: df.identity.uspLabel },
    personalTraits: { kind: 'traits', label: df.identity.personalTraitsLabel },
    notes: { kind: 'text', label: df.common.notesLabel },
  },
  activity: {
    valueProposition: { kind: 'text', label: df.activity.valuePropositionLabel },
    targetCustomers: { kind: 'text', label: df.activity.targetCustomersLabel },
    topClientsByRevenue: { kind: 'topClients', label: df.activity.topClientsLabel },
    hasRecurringRevenue: { kind: 'bool', label: df.activity.hasRecurringRevenueLabel },
    recurringRevenueSharePercent: {
      kind: 'num',
      suffix: df.activity.recurringShareSuffix,
      label: df.activity.recurringShareLabel,
    },
    scalability: {
      kind: 'enum',
      options: df.activity.scalabilityOptions,
      label: df.activity.scalabilityLabel,
    },
    hasDocumentedSalesProcess: {
      kind: 'bool',
      label: df.activity.hasDocumentedSalesProcessLabel,
    },
    notes: { kind: 'text', label: df.common.notesLabel },
  },
  style: {
    targetGroupDefined: { kind: 'bool', label: df.style.targetGroupDefinedLabel },
    targetGroupDescription: { kind: 'text', label: df.style.targetGroupDescriptionLabel },
    competitors: { kind: 'stringList', label: df.style.competitorsLabel },
    differentiators: { kind: 'text', label: df.style.differentiatorsLabel },
    selfReportedHourlyRateEuro: {
      kind: 'num',
      suffix: df.style.selfRateSuffix,
      label: df.style.selfRateLabel,
    },
    industryFloorHourlyRateEuro: {
      kind: 'num',
      suffix: df.style.floorRateSuffix,
      label: df.style.floorRateLabel,
    },
    pricingStrategyDocumented: { kind: 'bool', label: df.style.pricingStrategyDocumentedLabel },
    notes: { kind: 'text', label: df.common.notesLabel },
  },
  health: {
    selfReportedResilienceScore: { kind: 'num', label: df.health.resilienceLabel },
    hoursWorkedPerWeek: {
      kind: 'num',
      suffix: df.health.hoursPerWeekSuffix,
      label: df.health.hoursPerWeekLabel,
    },
    hasMedicalCertification: { kind: 'bool', label: df.health.hasMedicalCertificationLabel },
    chronicConditionsExist: { kind: 'bool', label: df.health.chronicConditionsLabel },
    conditionsAffectWork: { kind: 'bool', label: df.health.conditionsAffectWorkLabel },
    copingStrategiesDescribed: { kind: 'text', label: df.health.copingLabel },
    pastCrisisHandlingNotes: { kind: 'text', label: df.health.pastCrisisLabel },
    notes: { kind: 'text', label: df.common.notesLabel },
  },
  private: {
    childcareStatus: {
      kind: 'enum',
      options: df.private.childcareOptions,
      label: df.private.childcareStatusLabel,
    },
    hasCareResponsibility: { kind: 'bool', label: df.private.hasCareResponsibilityLabel },
    careResponsibilityDescription: { kind: 'text', label: df.private.careDescriptionLabel },
    zumutbarkeitsgruende: { kind: 'text', label: df.private.zumutbarkeitLabel },
    regionalLaborMarketContext: { kind: 'text', label: df.private.regionalContextLabel },
    notes: { kind: 'text', label: df.common.notesLabel },
  },
};

interface QualificationLike {
  readonly type?: string;
  readonly title?: string;
  readonly yearObtained?: number | null;
  readonly industryRelevant?: boolean;
}
interface TopClientLike {
  readonly id?: string;
  readonly revenueSharePercent?: number | null;
}

function formatNumber(n: number): string {
  return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

function formatValue(spec: FieldSpec, raw: string): string | null {
  switch (spec.kind) {
    case 'text': {
      const t = raw.trim();
      return t.length > 0 ? t : null;
    }
    case 'bool': {
      const b = parseOptionalBool(raw);
      if (b === undefined) return null;
      return b ? YES : NO;
    }
    case 'num': {
      const n = parseNum(raw);
      if (n === undefined) return null;
      return spec.suffix ? `${formatNumber(n)} ${spec.suffix}` : formatNumber(n);
    }
    case 'enum': {
      const key = raw.trim();
      if (key.length === 0) return null;
      return spec.options[key] ?? key;
    }
    case 'traits': {
      const items = parseList<string>(raw);
      if (items.length === 0) return null;
      const opts = df.identity.traitOptions as Readonly<Record<string, string>>;
      return items.map((t) => opts[t] ?? t).join(', ');
    }
    case 'qualifications': {
      const items = parseList<QualificationLike>(raw);
      if (items.length === 0) return null;
      const typeOpts = df.identity.qualTypeOptions as Readonly<Record<string, string>>;
      const lines = items
        .map((q) => {
          const title = (q.title ?? '').trim();
          const typeLabel = q.type ? (typeOpts[q.type] ?? q.type) : '';
          const meta = [typeLabel, q.yearObtained ? String(q.yearObtained) : '']
            .filter((x) => x.length > 0)
            .join(', ');
          const rel = q.industryRelevant ? ` · ${df.identity.qualIndustryRelevant}` : '';
          const head = title.length > 0 ? title : typeLabel;
          if (head.length === 0) return null;
          return meta.length > 0 && title.length > 0 ? `${title} (${meta})${rel}` : `${head}${rel}`;
        })
        .filter((x): x is string => x !== null);
      return lines.length > 0 ? lines.join('\n') : null;
    }
    case 'topClients': {
      const items = parseList<TopClientLike>(raw);
      if (items.length === 0) return null;
      const lines = items
        .map((c) => {
          const id = (c.id ?? '').trim();
          if (id.length === 0) return null;
          const share =
            c.revenueSharePercent !== null && c.revenueSharePercent !== undefined
              ? ` – ${formatNumber(c.revenueSharePercent)} %`
              : '';
          return `${id}${share}`;
        })
        .filter((x): x is string => x !== null);
      return lines.length > 0 ? lines.join('\n') : null;
    }
    case 'stringList': {
      const items = parseList<string>(raw);
      const cleaned = items.map((s) => s.trim()).filter((s) => s.length > 0);
      return cleaned.length > 0 ? cleaned.join(', ') : null;
    }
  }
}

export interface PresentedField {
  readonly label: string;
  readonly display: string;
}

/**
 * Wandelt (Dimension, Feldschlüssel, Rohwert) in { label, display } um.
 * Gibt null zurück, wenn kein Mapping existiert ODER der Wert leer ist —
 * die aufrufende Sicht überspringt solche Felder.
 */
export function presentDimensionField(
  dimension: DimensionKey,
  fieldKey: string,
  raw: string,
): PresentedField | null {
  if (dimension === 'resources') return null; // Resources hat eigene Detail-Anzeige.
  const dimSpecs = SPECS[dimension];
  const spec = dimSpecs[fieldKey];
  if (!spec) return null;
  const display = formatValue(spec, raw);
  if (display === null) return null;
  return { label: spec.label, display };
}

/**
 * Präsentiert alle nicht-leeren Felder einer Dimension in stabiler Reihenfolge
 * (Schlüssel-Reihenfolge der Spezifikation = fachliche Reihenfolge).
 */
export function presentDimensionFields(
  dimension: DimensionKey,
  fields: Readonly<Record<string, string>>,
): readonly PresentedField[] {
  if (dimension === 'resources') return [];
  const dimSpecs = SPECS[dimension];
  const out: PresentedField[] = [];
  for (const key of Object.keys(dimSpecs)) {
    const raw = fields[key];
    if (raw === undefined) continue;
    const p = presentDimensionField(dimension, key, raw);
    if (p) out.push(p);
  }
  return out;
}
